// Mesures produit : les chiffres relevés par les connecteurs (PostHog, GA4,
// Matomo, Jira) et ceux saisis à la main (revenus, conversion, chiffre
// interne), chacun avec sa mini-courbe.
//
// Une valeur seule ne dit pas si « ça a bougé » : chaque carte montre la
// dernière valeur, l'écart au relevé précédent et les derniers relevés. Les
// ordonnées de la courbe sont calculées par le serveur ; ici, on ne fait que
// les poser dans un cadre.
import { useState, type FormEvent } from 'react';
import { ApiRequestError } from '../api/client';
import { Button, ErrorBanner, Eyebrow, Field } from '../components/ui';
import {
  useAddMeasure,
  useMeasureHistory,
  useRemoveMeasure,
  type MeasureHistory,
} from './measures';
import { useProductContext } from '../api/queries';
import { ScoreForm } from './ScoreForm';

const fmtNumber = (v: number) => v.toLocaleString('fr-FR', { maximumFractionDigits: 2 });
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });

const SPARK_W = 120;
const SPARK_H = 34;
const SPARK_PAD = 4;

/** Mini-courbe : un point par relevé, reliés. Un seul relevé = un point au centre. */
function Sparkline({ measure }: { measure: MeasureHistory }) {
  const n = measure.spark.length;
  if (n === 0) return null;
  const xy = measure.spark.map((s, i) => ({
    x: n === 1 ? SPARK_W / 2 : SPARK_PAD + (i / (n - 1)) * (SPARK_W - 2 * SPARK_PAD),
    y: SPARK_PAD + (1 - s) * (SPARK_H - 2 * SPARK_PAD),
    point: measure.points[i],
  }));
  const unit = measure.unit ? ` ${measure.unit}` : '';
  return (
    <svg
      className="spark"
      viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
      width={SPARK_W}
      height={SPARK_H}
      role="img"
      aria-label={`Évolution sur ${n} relevé${n > 1 ? 's' : ''} : ${measure.points
        .map((p) => fmtNumber(p.value))
        .join(', ')}`}
    >
      {n > 1 ? (
        <polyline className="spark-line" points={xy.map((p) => `${p.x},${p.y}`).join(' ')} />
      ) : null}
      {xy.map((p, i) => (
        <circle key={i} className={`spark-dot${i === n - 1 ? ' spark-dot-last' : ''}`} cx={p.x} cy={p.y} r={i === n - 1 ? 3 : 2}>
          {p.point ? <title>{`${fmtDate(p.point.at)} : ${fmtNumber(p.point.value)}${unit}`}</title> : null}
        </circle>
      ))}
    </svg>
  );
}

function MeasureCard({
  measure,
  canWrite,
  onRemoveLast,
  removing,
}: {
  measure: MeasureHistory;
  canWrite: boolean;
  onRemoveLast: (id: string) => void;
  removing: boolean;
}) {
  return (
    <li className="measure">
      <span className="metric-label">{measure.name}</span>
      <span className="metric-row">
        <span className="metric-value">
          {fmtNumber(measure.value)}
          {measure.unit ? <span className="metric-unit">{measure.unit}</span> : null}
        </span>
        {measure.change ? (
          <span className="metric-trend tone-neutral" title="Écart au relevé précédent">
            {measure.change.label}
          </span>
        ) : null}
      </span>
      <Sparkline measure={measure} />
      <span className="metric-detail">
        {measure.sourceLabel} · {measure.count} relevé{measure.count > 1 ? 's' : ''} · dernier le{' '}
        {fmtDate(measure.lastAt)}
      </span>
      {measure.period ? <span className="metric-hint">{measure.period}</span> : null}
      {canWrite && measure.manual && measure.lastId ? (
        <Button variant="link" size="s"
          type="button"
          className="measure-undo"
          disabled={removing}
          onClick={() => {
            if (measure.lastId && window.confirm('Retirer le dernier relevé de cette mesure ?')) {
              onRemoveLast(measure.lastId);
            }
          }}
        >
          Retirer le dernier relevé
        </Button>
      ) : null}
    </li>
  );
}

/** Saisie d'un chiffre que nul connecteur ne remonte. Même nom = même courbe. */
function ManualMeasureForm({
  projectId,
  manualNames,
  onDone,
}: {
  projectId: string;
  manualNames: string[];
  onDone: () => void;
}) {
  const add = useAddMeasure(projectId);
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [unit, setUnit] = useState('');
  const [period, setPeriod] = useState('');
  const [recordedOn, setRecordedOn] = useState('');
  const today = new Date().toISOString().slice(0, 10);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    add.mutate(
      { name, value, unit, period, recordedOn },
      { onSuccess: () => onDone() },
    );
  };

  return (
    <form className="zy-card measure-form" onSubmit={submit}>
      <p className="card-lede">
        Un chiffre que vos outils ne remontent pas : revenus, taux de conversion, chiffre interne.
        Reprenez le même nom pour ajouter un point à une courbe existante.
      </p>
      <div className="measure-form-grid">
        <Field
          label="Nom de la mesure"
          value={name}
          onChange={(e) => setName(e.target.value)}
          list="measure-names"
          placeholder="Chiffre d’affaires mensuel"
          required
          maxLength={80}
        />
        <datalist id="measure-names">
          {manualNames.map((n) => (
            <option key={n} value={n} />
          ))}
        </datalist>
        <Field
          label="Valeur"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          inputMode="decimal"
          placeholder="42 000"
          required
          voice={false}
        />
        <Field
          label="Unité"
          value={unit}
          onChange={(e) => setUnit(e.target.value)}
          placeholder="€, %, clients…"
          maxLength={20}
          voice={false}
        />
        <Field
          label="Période"
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          placeholder="septembre 2026"
          maxLength={40}
          hint="Facultatif"
        />
        <Field
          label="Date du relevé"
          type="date"
          value={recordedOn}
          max={today}
          onChange={(e) => setRecordedOn(e.target.value)}
          hint="Vide = aujourd’hui"
        />
      </div>
      {add.error ? (
        <ErrorBanner>
          {add.error instanceof ApiRequestError ? add.error.message : 'Enregistrement impossible.'}
        </ErrorBanner>
      ) : null}
      <div className="measure-form-actions">
        <Button variant="accent" type="submit" loading={add.isPending}>
          Enregistrer la mesure
        </Button>
        <Button type="button" variant="ghost" onClick={onDone}>
          Annuler
        </Button>
      </div>
    </form>
  );
}

export function MeasureHistorySection({ projectId }: { projectId: string }) {
  const history = useMeasureHistory(projectId);
  const context = useProductContext(projectId);
  const remove = useRemoveMeasure(projectId);
  const [adding, setAdding] = useState(false);
  const [scoring, setScoring] = useState(false);

  // Une erreur de lecture ne doit pas masquer le reste du tableau de bord.
  if (history.isPending || history.isError) return null;
  const { measures, canWrite } = history.data;
  // Les mesures que le modèle attend d'abord : saisies sous ce nom, elles nourrissent
  // leur pilier. Puis celles déjà saisies, pour rester sur la même courbe.
  const product = context.data;
  const expected = product?.product.pillarModel
    ? product.catalog.models[product.product.pillarModel].pillars.flatMap((p) => p.measures)
      .filter((name) => product.catalog.manualMeasures.includes(name) && !product.catalog.models[product.product.pillarModel!].excluded.includes(name))
    : [];
  const manualNames = [...new Set([...expected, ...measures.filter((m) => m.manual).map((m) => m.name)])];

  return (
    <section className="metrics measures">
      <div className="metrics-head">
        <Eyebrow>Impact produit</Eyebrow>
        <h2 className="t-h2">Mesures produit</h2>
        <span className="t-mono">un point par synchronisation ou saisie</span>
        {canWrite && !adding && !scoring ? (
          <>
            <Button variant="ghost" onClick={() => setAdding(true)}>
              Saisir une mesure
            </Button>
            <Button variant="ghost" onClick={() => setScoring(true)}>
              Calculer un NPS ou un CSAT
            </Button>
          </>
        ) : null}
      </div>

      {adding ? (
        <ManualMeasureForm projectId={projectId} manualNames={manualNames} onDone={() => setAdding(false)} />
      ) : null}
      {scoring ? <ScoreForm projectId={projectId} onDone={() => setScoring(false)} /> : null}

      {remove.error ? (
        <ErrorBanner>
          {remove.error instanceof ApiRequestError ? remove.error.message : 'Retrait impossible.'}
        </ErrorBanner>
      ) : null}

      {measures.length === 0 ? (
        <p className="card-lede measures-empty">
          Aucune mesure relevée pour l’instant. Synchronisez un connecteur (Options du projet) ou
          saisissez un chiffre à la main : chaque relevé ajoute un point à la courbe.
        </p>
      ) : (
        <ul className="metric-grid">
          {measures.map((m) => (
            <MeasureCard
              key={m.key}
              measure={m}
              canWrite={canWrite}
              onRemoveLast={(id) => remove.mutate(id)}
              removing={remove.isPending}
            />
          ))}
        </ul>
      )}
    </section>
  );
}
